'use client';

import React, { useState, useMemo } from 'react';
import { FileText, Plus, Search, Filter, ChevronDown, X, Clock, CheckCircle2, AlertTriangle, Edit3, Eye, Calendar, Building2, User, Tag, Shield, Lock, Globe, Bell, BookOpen, ChevronRight, ArrowRight, TrendingUp, Zap, Download, Copy, CheckSquare, AlertCircle, FileCheck, Layers, Target, BarChart2, ArrowLeft } from 'lucide-react';
import { useRole } from '@/contexts/RoleContext';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';


// ─── Types ────────────────────────────────────────────────────────────────────

type ContractStatus = 'Draft' | 'Negotiation' | 'Pending Signature' | 'Active' | 'Expiring';

interface SLAObligation {
  metric: string;
  target: string;
  current: string;
  status: 'Met' | 'At Risk' | 'Breached';
}

interface Deliverable {
  id: string;
  title: string;
  dueDate: string;
  status: 'Pending' | 'In Progress' | 'Completed' | 'Overdue';
  owner: string;
}

interface ClauseItem {
  id: string;
  type: 'Confidentiality' | 'Security' | 'Privacy' | 'Audit Rights' | 'Incident Notification';
  title: string;
  summary: string;
  included: boolean;
  lastReviewed: string;
}

interface TimelineEvent {
  date: string;
  event: string;
  actor: string;
  type: 'created' | 'updated' | 'signed' | 'approved' | 'alert' | 'milestone';
}

interface Contract {
  id: string;
  contractRef: string;
  title: string;
  vendorName: string;
  vendorId: string;
  status: ContractStatus;
  value: number;
  startDate: string;
  endDate: string;
  renewalDate: string;
  owner: string;
  department: string;
  category: string;
  scopeOfWork: string;
  deliverables: Deliverable[];
  slaObligations: SLAObligation[];
  clauses: ClauseItem[];
  timeline: TimelineEvent[];
  daysUntilExpiry?: number;
  autoRenew: boolean;
  signedBy?: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_CONTRACTS: Contract[] = [
  {
    id: 'c001',
    contractRef: 'CTR-2025-001',
    title: 'Enterprise Cloud Infrastructure Services',
    vendorName: 'TechCore Solutions',
    vendorId: 'v001',
    status: 'Active',
    value: 2400000,
    startDate: '2024-01-15',
    endDate: '2026-01-14',
    renewalDate: '2025-10-14',
    owner: 'Sarah Chen',
    department: 'IT Infrastructure',
    category: 'Cloud Services',
    autoRenew: true,
    signedBy: 'Mayor J. Williams',
    scopeOfWork: 'Provision and management of cloud infrastructure including compute, storage, networking, and managed security services for all city departments. Includes 24/7 NOC support, disaster recovery, and quarterly capacity reviews.',
    deliverables: [
      { id: 'd1', title: 'Monthly Uptime Report', dueDate: '2025-04-30', status: 'In Progress', owner: 'TechCore NOC' },
      { id: 'd2', title: 'Q2 Capacity Review', dueDate: '2025-06-30', status: 'Pending', owner: 'Sarah Chen' },
      { id: 'd3', title: 'Annual Security Audit', dueDate: '2025-12-31', status: 'Pending', owner: 'IT Security' },
      { id: 'd4', title: 'DR Test Report', dueDate: '2025-03-31', status: 'Completed', owner: 'TechCore NOC' },
    ],
    slaObligations: [
      { metric: 'System Uptime', target: '99.9%', current: '99.95%', status: 'Met' },
      { metric: 'Incident Response (P1)', target: '< 15 min', current: '12 min avg', status: 'Met' },
      { metric: 'Ticket Resolution (P2)', target: '< 4 hrs', current: '5.2 hrs avg', status: 'At Risk' },
      { metric: 'Backup Recovery Time', target: '< 2 hrs', current: '1.8 hrs', status: 'Met' },
    ],
    clauses: [
      { id: 'cl1', type: 'Confidentiality', title: 'Non-Disclosure Agreement', summary: 'Vendor shall not disclose city data or operational details to third parties without written consent. Covers all municipal records, citizen PII, and infrastructure configurations.', included: true, lastReviewed: '2024-01-10' },
      { id: 'cl2', type: 'Security', title: 'Information Security Standards', summary: 'Vendor must maintain SOC 2 Type II certification, implement AES-256 encryption at rest and in transit, and conduct annual penetration testing with results shared within 30 days.', included: true, lastReviewed: '2024-01-10' },
      { id: 'cl3', type: 'Privacy', title: 'Data Privacy & CCPA Compliance', summary: 'Vendor acts as a data processor under CCPA. All citizen PII must be processed only as directed, with data minimization principles applied. DPA addendum attached.', included: true, lastReviewed: '2024-01-10' },
      { id: 'cl4', type: 'Audit Rights', title: 'Right to Audit', summary: 'City retains the right to conduct or commission audits of vendor systems, processes, and records with 30 days written notice. Vendor must provide full cooperation and documentation access.', included: true, lastReviewed: '2024-01-10' },
      { id: 'cl5', type: 'Incident Notification', title: 'Security Incident Notification', summary: 'Vendor must notify City within 72 hours of discovering any security incident or data breach affecting city systems. Notification must include scope, impact assessment, and remediation plan.', included: true, lastReviewed: '2024-01-10' },
    ],
    timeline: [
      { date: '2024-01-15', event: 'Contract executed and activated', actor: 'Mayor J. Williams', type: 'signed' },
      { date: '2024-01-10', event: 'Final terms approved by City Attorney', actor: 'City Attorney Office', type: 'approved' },
      { date: '2023-12-20', event: 'Negotiation completed — pricing finalized', actor: 'Sarah Chen', type: 'updated' },
      { date: '2023-11-15', event: 'Contract draft created', actor: 'Procurement Office', type: 'created' },
    ],
  },
  {
    id: 'c002',
    contractRef: 'CTR-2025-002',
    title: 'Cybersecurity Managed Detection & Response',
    vendorName: 'SecureNet Defense',
    vendorId: 'v002',
    status: 'Expiring',
    value: 890000,
    startDate: '2023-04-01',
    endDate: '2025-03-31',
    renewalDate: '2025-01-31',
    owner: 'Marcus Johnson',
    department: 'IT Security',
    category: 'Cybersecurity',
    autoRenew: false,
    daysUntilExpiry: 2,
    signedBy: 'City Manager R. Torres',
    scopeOfWork: 'Managed Detection and Response (MDR) services covering 24/7 threat monitoring, incident response, threat hunting, and monthly executive threat briefings for all city networks and endpoints.',
    deliverables: [
      { id: 'd1', title: 'Monthly Threat Intelligence Report', dueDate: '2025-03-31', status: 'In Progress', owner: 'SecureNet SOC' },
      { id: 'd2', title: 'Renewal Proposal Submission', dueDate: '2025-01-31', status: 'Overdue', owner: 'Marcus Johnson' },
      { id: 'd3', title: 'Final Service Transition Plan', dueDate: '2025-03-15', status: 'Pending', owner: 'IT Security' },
    ],
    slaObligations: [
      { metric: 'Threat Detection Time', target: '< 5 min', current: '4.2 min avg', status: 'Met' },
      { metric: 'Incident Containment', target: '< 1 hr', current: '47 min avg', status: 'Met' },
      { metric: 'False Positive Rate', target: '< 5%', current: '7.1%', status: 'Breached' },
      { metric: 'Monthly Report Delivery', target: 'By 5th', current: 'By 3rd avg', status: 'Met' },
    ],
    clauses: [
      { id: 'cl1', type: 'Confidentiality', title: 'Threat Intelligence Confidentiality', summary: 'All threat intelligence, vulnerability data, and incident details are classified as confidential. Vendor may not use city threat data for benchmarking or product development without explicit consent.', included: true, lastReviewed: '2023-03-25' },
      { id: 'cl2', type: 'Security', title: 'SOC Operations Security', summary: 'Vendor SOC analysts must pass background checks, maintain security clearances, and operate from certified facilities. Remote access to city systems requires MFA and VPN.', included: true, lastReviewed: '2023-03-25' },
      { id: 'cl3', type: 'Privacy', title: 'Log Data Privacy', summary: 'Network logs and endpoint telemetry containing citizen data must be processed under strict data minimization. Retention limited to 90 days unless required for active investigation.', included: true, lastReviewed: '2023-03-25' },
      { id: 'cl4', type: 'Audit Rights', title: 'SOC Audit Access', summary: 'City may audit SOC operations, analyst certifications, and tool configurations annually. Vendor must provide evidence of compliance with NIST CSF within 15 business days of request.', included: true, lastReviewed: '2023-03-25' },
      { id: 'cl5', type: 'Incident Notification', title: 'Real-Time Incident Escalation', summary: 'Critical incidents (P1) require immediate phone notification to CISO within 15 minutes. Written incident report within 4 hours. Post-incident review within 5 business days.', included: true, lastReviewed: '2023-03-25' },
    ],
    timeline: [
      { date: '2025-01-31', event: 'Renewal deadline passed — action required', actor: 'System', type: 'alert' },
      { date: '2024-10-01', event: 'Renewal reminder sent to vendor', actor: 'Marcus Johnson', type: 'updated' },
      { date: '2023-04-01', event: 'Contract activated', actor: 'City Manager R. Torres', type: 'signed' },
      { date: '2023-03-28', event: 'Legal review completed', actor: 'City Attorney Office', type: 'approved' },
      { date: '2023-03-01', event: 'Contract draft initiated', actor: 'IT Security', type: 'created' },
    ],
  },
  {
    id: 'c003',
    contractRef: 'CTR-2025-003',
    title: 'Citizen Data Analytics Platform',
    vendorName: 'DataBridge Analytics',
    vendorId: 'v003',
    status: 'Negotiation',
    value: 560000,
    startDate: '',
    endDate: '',
    renewalDate: '',
    owner: 'Elena Rodriguez',
    department: 'City Planning',
    category: 'Data & Analytics',
    autoRenew: false,
    scopeOfWork: 'Implementation and operation of a citizen-facing data analytics platform for urban planning insights, traffic pattern analysis, and public service optimization. Includes data ingestion pipelines, dashboards, and API integrations with existing city systems.',
    deliverables: [
      { id: 'd1', title: 'Redlined Contract Draft', dueDate: '2025-04-15', status: 'In Progress', owner: 'City Attorney' },
      { id: 'd2', title: 'Privacy Impact Assessment', dueDate: '2025-04-30', status: 'Pending', owner: 'Privacy Officer' },
      { id: 'd3', title: 'Technical Architecture Review', dueDate: '2025-04-20', status: 'In Progress', owner: 'IT Security' },
    ],
    slaObligations: [
      { metric: 'Platform Availability', target: '99.5%', current: 'TBD', status: 'Met' },
      { metric: 'Data Refresh Latency', target: '< 4 hrs', current: 'TBD', status: 'Met' },
      { metric: 'API Response Time', target: '< 200ms', current: 'TBD', status: 'Met' },
    ],
    clauses: [
      { id: 'cl1', type: 'Confidentiality', title: 'Citizen Data Confidentiality', summary: 'All citizen-derived data, including anonymized datasets, must be treated as confidential. Vendor may not commercialize, sell, or share city data with third parties.', included: true, lastReviewed: '2025-03-01' },
      { id: 'cl2', type: 'Security', title: 'Data Platform Security', summary: 'Platform must achieve FedRAMP Moderate authorization within 6 months of go-live. All data encrypted at rest (AES-256) and in transit (TLS 1.3+).', included: true, lastReviewed: '2025-03-01' },
      { id: 'cl3', type: 'Privacy', title: 'CCPA & State Privacy Compliance', summary: 'Vendor must comply with CCPA, California Privacy Rights Act (CPRA), and applicable municipal privacy ordinances. Data subject request handling SLA: 45 days.', included: true, lastReviewed: '2025-03-01' },
      { id: 'cl4', type: 'Audit Rights', title: 'Data Processing Audit Rights', summary: 'City retains right to audit data processing activities, retention schedules, and deletion procedures. Annual audit with 14-day notice. Vendor must maintain audit logs for 3 years.', included: false, lastReviewed: '2025-03-01' },
      { id: 'cl5', type: 'Incident Notification', title: 'Data Breach Notification Protocol', summary: 'Any breach affecting citizen data requires notification within 48 hours (stricter than CCPA baseline). Includes preliminary assessment, affected record count, and containment steps.', included: true, lastReviewed: '2025-03-01' },
    ],
    timeline: [
      { date: '2025-03-20', event: 'Negotiation round 2 — pricing under review', actor: 'Elena Rodriguez', type: 'updated' },
      { date: '2025-03-01', event: 'Initial terms submitted by vendor', actor: 'DataBridge Analytics', type: 'updated' },
      { date: '2025-02-15', event: 'Contract draft created from RFP award', actor: 'Procurement Office', type: 'created' },
    ],
  },
  {
    id: 'c004',
    contractRef: 'CTR-2025-004',
    title: 'HR & Payroll System Modernization',
    vendorName: 'WorkForce Pro',
    vendorId: 'v004',
    status: 'Pending Signature',
    value: 1200000,
    startDate: '',
    endDate: '2028-06-30',
    renewalDate: '2028-03-31',
    owner: 'David Kim',
    department: 'Human Resources',
    category: 'HR Technology',
    autoRenew: true,
    scopeOfWork: 'Full replacement of legacy HR and payroll systems with a modern SaaS platform. Includes implementation, data migration, training, and 3-year managed service for 1,200 city employees across all departments.',
    deliverables: [
      { id: 'd1', title: 'Signed Contract Package', dueDate: '2025-04-10', status: 'Pending', owner: 'City Manager' },
      { id: 'd2', title: 'Implementation Kickoff', dueDate: '2025-05-01', status: 'Pending', owner: 'David Kim' },
      { id: 'd3', title: 'Data Migration Plan', dueDate: '2025-05-15', status: 'Pending', owner: 'WorkForce Pro' },
    ],
    slaObligations: [
      { metric: 'Payroll Processing Accuracy', target: '99.99%', current: 'TBD', status: 'Met' },
      { metric: 'System Availability', target: '99.9%', current: 'TBD', status: 'Met' },
      { metric: 'Support Response (P1)', target: '< 2 hrs', current: 'TBD', status: 'Met' },
    ],
    clauses: [
      { id: 'cl1', type: 'Confidentiality', title: 'Employee Data Confidentiality', summary: 'All employee PII, salary data, and HR records are strictly confidential. Vendor may not use employee data for any purpose other than service delivery. Survives contract termination for 7 years.', included: true, lastReviewed: '2025-03-15' },
      { id: 'cl2', type: 'Security', title: 'HR System Security Requirements', summary: 'System must maintain SOC 2 Type II, support SSO/SAML 2.0, enforce MFA for all admin access, and provide role-based access controls aligned with city org structure.', included: true, lastReviewed: '2025-03-15' },
      { id: 'cl3', type: 'Privacy', title: 'Employee Privacy Rights', summary: 'Vendor must support employee rights under applicable privacy laws including access, correction, and deletion requests. Data retention schedule must align with city records retention policy.', included: true, lastReviewed: '2025-03-15' },
      { id: 'cl4', type: 'Audit Rights', title: 'Payroll Audit Access', summary: 'Finance Director and external auditors must have read-only access to payroll records and processing logs. Annual compliance audit rights with 7-day notice for payroll-related reviews.', included: true, lastReviewed: '2025-03-15' },
      { id: 'cl5', type: 'Incident Notification', title: 'Employee Data Breach Protocol', summary: 'Breaches involving employee PII or payroll data require notification within 24 hours to HR Director and City Manager. Includes mandatory credit monitoring offer for affected employees.', included: true, lastReviewed: '2025-03-15' },
    ],
    timeline: [
      { date: '2025-03-28', event: 'Awaiting Mayor signature — routed for execution', actor: 'City Attorney Office', type: 'milestone' },
      { date: '2025-03-20', event: 'City Council approval received', actor: 'City Council', type: 'approved' },
      { date: '2025-03-10', event: 'Final legal review completed', actor: 'City Attorney Office', type: 'approved' },
      { date: '2025-02-20', event: 'Negotiation finalized', actor: 'David Kim', type: 'updated' },
      { date: '2025-01-30', event: 'Contract draft created', actor: 'Procurement Office', type: 'created' },
    ],
  },
  {
    id: 'c005',
    contractRef: 'CTR-2025-005',
    title: 'Public Safety Communications Upgrade',
    vendorName: 'FirstResponse Tech',
    vendorId: 'v005',
    status: 'Draft',
    value: 3100000,
    startDate: '',
    endDate: '',
    renewalDate: '',
    owner: 'James Park',
    department: 'Public Safety',
    category: 'Communications',
    autoRenew: false,
    scopeOfWork: 'Upgrade of city-wide public safety communications infrastructure including CAD system replacement, radio network modernization, and integration with regional emergency management systems.',
    deliverables: [
      { id: 'd1', title: 'Scope of Work Finalization', dueDate: '2025-05-01', status: 'In Progress', owner: 'James Park' },
      { id: 'd2', title: 'Technical Specifications Review', dueDate: '2025-05-15', status: 'Pending', owner: 'IT Security' },
      { id: 'd3', title: 'Budget Approval', dueDate: '2025-06-01', status: 'Pending', owner: 'Finance Director' },
    ],
    slaObligations: [
      { metric: 'System Availability', target: '99.999%', current: 'TBD', status: 'Met' },
      { metric: 'Failover Time', target: '< 30 sec', current: 'TBD', status: 'Met' },
    ],
    clauses: [
      { id: 'cl1', type: 'Confidentiality', title: 'Public Safety Data Confidentiality', summary: 'All CAD data, dispatch records, and communications logs are classified as sensitive public safety information. Strict need-to-know access controls required.', included: true, lastReviewed: '2025-03-25' },
      { id: 'cl2', type: 'Security', title: 'Critical Infrastructure Security', summary: 'System must comply with CJIS Security Policy, NIST SP 800-53 High baseline, and FirstNet standards. Annual third-party security assessment required.', included: true, lastReviewed: '2025-03-25' },
      { id: 'cl3', type: 'Privacy', title: 'Communications Privacy', summary: 'Radio communications and CAD records subject to state public records law with applicable exemptions for active investigations. Vendor must support redaction workflows.', included: false, lastReviewed: '2025-03-25' },
      { id: 'cl4', type: 'Audit Rights', title: 'System Audit Rights', summary: 'City and state oversight agencies retain full audit rights. Vendor must maintain immutable audit logs for all system access and configuration changes for 5 years.', included: true, lastReviewed: '2025-03-25' },
      { id: 'cl5', type: 'Incident Notification', title: 'Critical System Incident Protocol', summary: 'Any outage or security incident affecting public safety communications requires immediate (within 15 minutes) notification to Police Chief, Fire Chief, and City Manager.', included: true, lastReviewed: '2025-03-25' },
    ],
    timeline: [
      { date: '2025-03-25', event: 'Initial draft created from RFP specifications', actor: 'James Park', type: 'created' },
    ],
  },
  {
    id: 'c006',
    contractRef: 'CTR-2024-018',
    title: 'Document Management & Records System',
    vendorName: 'RecordKeep Systems',
    vendorId: 'v006',
    status: 'Active',
    value: 420000,
    startDate: '2024-07-01',
    endDate: '2027-06-30',
    renewalDate: '2027-03-31',
    owner: 'Lisa Wong',
    department: 'City Clerk',
    category: 'Document Management',
    autoRenew: true,
    signedBy: 'City Manager R. Torres',
    scopeOfWork: 'Enterprise document management and records retention system for all city departments. Includes e-filing, automated retention scheduling, public records request portal, and integration with city ERP.',
    deliverables: [
      { id: 'd1', title: 'Q1 Usage & Performance Report', dueDate: '2025-04-15', status: 'In Progress', owner: 'RecordKeep Systems' },
      { id: 'd2', title: 'Annual Records Audit', dueDate: '2025-07-01', status: 'Pending', owner: 'Lisa Wong' },
    ],
    slaObligations: [
      { metric: 'System Availability', target: '99.5%', current: '99.8%', status: 'Met' },
      { metric: 'Search Response Time', target: '< 3 sec', current: '1.4 sec avg', status: 'Met' },
      { metric: 'Records Request Fulfillment', target: '< 10 days', current: '8 days avg', status: 'Met' },
    ],
    clauses: [
      { id: 'cl1', type: 'Confidentiality', title: 'Municipal Records Confidentiality', summary: 'Vendor must maintain confidentiality of all non-public records including personnel files, attorney-client privileged documents, and law enforcement records.', included: true, lastReviewed: '2024-06-20' },
      { id: 'cl2', type: 'Security', title: 'Records System Security', summary: 'System must support role-based access, maintain tamper-evident audit trails, and comply with NARA guidelines for electronic records management.', included: true, lastReviewed: '2024-06-20' },
      { id: 'cl3', type: 'Privacy', title: 'PII in Records', summary: 'Citizen PII within records must be handled per CCPA. System must support automated PII detection and redaction for public records responses.', included: true, lastReviewed: '2024-06-20' },
      { id: 'cl4', type: 'Audit Rights', title: 'Records Audit Trail', summary: 'Complete audit trail of all record access, modifications, and deletions. City Clerk and external auditors have unrestricted read access to audit logs.', included: true, lastReviewed: '2024-06-20' },
      { id: 'cl5', type: 'Incident Notification', title: 'Records Breach Notification', summary: 'Unauthorized access to or disclosure of city records requires notification within 48 hours. Includes scope of affected records and immediate access revocation steps.', included: true, lastReviewed: '2024-06-20' },
    ],
    timeline: [
      { date: '2024-07-01', event: 'Contract activated — go-live', actor: 'City Manager R. Torres', type: 'signed' },
      { date: '2024-06-25', event: 'Final approval from City Council', actor: 'City Council', type: 'approved' },
      { date: '2024-06-10', event: 'Legal review and redlines resolved', actor: 'City Attorney Office', type: 'updated' },
      { date: '2024-05-15', event: 'Contract draft initiated', actor: 'Lisa Wong', type: 'created' },
    ],
  },
];

// ─── Config ───────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<ContractStatus, { label: string; dot: string; text: string; bg: string; border: string }> = {
  Draft:             { label: 'Draft',            dot: 'bg-slate-400',  text: 'text-slate-600 dark:text-slate-400',  bg: 'bg-slate-50 dark:bg-slate-900/40',  border: 'border-slate-200 dark:border-slate-700' },
  Negotiation:       { label: 'Negotiation',       dot: 'bg-amber-500',  text: 'text-amber-700 dark:text-amber-400',  bg: 'bg-amber-50 dark:bg-amber-950/40',  border: 'border-amber-200 dark:border-amber-800' },
  'Pending Signature': { label: 'Pending Signature', dot: 'bg-purple-500', text: 'text-purple-700 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-950/40', border: 'border-purple-200 dark:border-purple-800' },
  Active:            { label: 'Active',            dot: 'bg-teal-500',   text: 'text-teal-700 dark:text-teal-400',   bg: 'bg-teal-50 dark:bg-teal-950/40',   border: 'border-teal-200 dark:border-teal-800' },
  Expiring:          { label: 'Expiring',          dot: 'bg-red-500',    text: 'text-red-700 dark:text-red-400',    bg: 'bg-red-50 dark:bg-red-950/40',    border: 'border-red-200 dark:border-red-800' },
};

const CLAUSE_ICONS: Record<ClauseItem['type'], React.ElementType> = {
  Confidentiality: Lock,
  Security: Shield,
  Privacy: Globe,
  'Audit Rights': FileCheck,
  'Incident Notification': Bell,
};

const CLAUSE_COLORS: Record<ClauseItem['type'], string> = {
  Confidentiality: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40',
  Security: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40',
  Privacy: 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-950/40',
  'Audit Rights': 'text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-950/40',
  'Incident Notification': 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/40',
};

const DELIVERABLE_STATUS_CONFIG = {
  Pending:    { text: 'text-slate-600 dark:text-slate-400', bg: 'bg-slate-100 dark:bg-slate-800' },
  'In Progress': { text: 'text-blue-700 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-950/40' },
  Completed:  { text: 'text-teal-700 dark:text-teal-400', bg: 'bg-teal-50 dark:bg-teal-950/40' },
  Overdue:    { text: 'text-red-700 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-950/40' },
};

const SLA_STATUS_CONFIG = {
  Met:      { text: 'text-teal-700 dark:text-teal-400', bg: 'bg-teal-50 dark:bg-teal-950/40', dot: 'bg-teal-500' },
  'At Risk': { text: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/40', dot: 'bg-amber-500' },
  Breached: { text: 'text-red-700 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-950/40', dot: 'bg-red-500' },
};

const TIMELINE_ICONS: Record<TimelineEvent['type'], React.ElementType> = {
  created: FileText,
  updated: Edit3,
  signed: CheckCircle2,
  approved: CheckSquare,
  alert: AlertCircle,
  milestone: Target,
};

const TIMELINE_COLORS: Record<TimelineEvent['type'], string> = {
  created: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400',
  updated: 'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400',
  signed: 'bg-teal-50 dark:bg-teal-950/40 text-teal-600 dark:text-teal-400',
  approved: 'bg-green-50 dark:bg-green-950/40 text-green-600 dark:text-green-400',
  alert: 'bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400',
  milestone: 'bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400',
};

const ALL_STATUSES: ContractStatus[] = ['Draft', 'Negotiation', 'Pending Signature', 'Active', 'Expiring'];
const ALL_CATEGORIES = ['All', 'Cloud Services', 'Cybersecurity', 'Data & Analytics', 'HR Technology', 'Communications', 'Document Management'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysUntil(dateStr: string): number {
  if (!dateStr) return 0;
  const now = new Date();
  const target = new Date(dateStr);
  return Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: ContractStatus }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.text} ${cfg.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function KPICard({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color: string }) {
  return (
    <div className="card p-4">
      <p className="text-xs text-[hsl(var(--text-muted))] font-medium uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-[hsl(var(--text-muted))] mt-0.5">{sub}</p>}
    </div>
  );
}

// ─── Detail Panel Tabs ────────────────────────────────────────────────────────

type DetailTab = 'overview' | 'deliverables' | 'sla' | 'clauses' | 'timeline';

function ContractDetailPanel({ contract, onClose }: { contract: Contract; onClose: () => void }) {
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');

  const tabs: { key: DetailTab; label: string; icon: React.ElementType }[] = [
    { key: 'overview', label: 'Overview', icon: FileText },
    { key: 'deliverables', label: 'Deliverables', icon: CheckSquare },
    { key: 'sla', label: 'SLA / KPIs', icon: BarChart2 },
    { key: 'clauses', label: 'Clause Library', icon: BookOpen },
    { key: 'timeline', label: 'Timeline', icon: Calendar },
  ];

  function handleExportPDF() {
    const slaRows = contract.slaObligations.map(s =>
      `<tr><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb">${s.metric}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb">${s.target}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb">${s.current}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-weight:600;color:${s.status==='Met'?'#0d9488':s.status==='Breached'?'#dc2626':'#d97706'}">${s.status}</td></tr>`
    ).join('');
    const deliverableRows = contract.deliverables.map(d =>
      `<tr><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb">${d.title}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb">${d.owner}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb">${d.dueDate}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-weight:600;color:${d.status==='Completed'?'#0d9488':d.status==='Overdue'?'#dc2626':d.status==='In Progress'?'#2563eb':'#6b7280'}">${d.status}</td></tr>`
    ).join('');
    const clauseRows = contract.clauses.map(cl =>
      `<tr><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb">${cl.type}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb">${cl.title}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-weight:600;color:${cl.included?'#0d9488':'#6b7280'}">${cl.included?'Included':'Excluded'}</td><td style="padding:6px 8px;border-bottom:1px solid #e5e7eb">${cl.lastReviewed}</td></tr>`
    ).join('');
    const slaBreaches = contract.slaObligations.filter(s => s.status === 'Breached').length;
    const slaAtRisk = contract.slaObligations.filter(s => s.status === 'At Risk').length;
    const slaMet = contract.slaObligations.filter(s => s.status === 'Met').length;

    const html = `<!DOCTYPE html><html><head><title>${contract.contractRef} – Contract Summary</title>
    <style>
      body{font-family:system-ui,sans-serif;color:#111827;margin:0;padding:32px;font-size:13px}
      h1{font-size:20px;font-weight:700;margin:0 0 4px}
      h2{font-size:14px;font-weight:600;margin:24px 0 8px;color:#374151;border-bottom:2px solid #e5e7eb;padding-bottom:4px}
      .meta{color:#6b7280;font-size:12px;margin-bottom:24px}
      .kpi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:24px}
      .kpi{background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:12px}
      .kpi-label{font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:.05em;margin-bottom:4px}
      .kpi-value{font-size:18px;font-weight:700;color:#111827}
      table{width:100%;border-collapse:collapse;font-size:12px}
      th{text-align:left;padding:8px;background:#f3f4f6;font-weight:600;color:#374151;font-size:11px;text-transform:uppercase;letter-spacing:.05em}
      .scope{background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:12px;line-height:1.6;color:#374151}
      .footer{margin-top:32px;padding-top:16px;border-top:1px solid #e5e7eb;font-size:11px;color:#9ca3af;display:flex;justify-content:space-between}
      @media print{body{padding:16px}}
    </style></head><body>
    <h1>${contract.title}</h1>
    <div class="meta">${contract.contractRef} · ${contract.vendorName} · ${contract.department} · Status: ${contract.status}</div>
    <div class="kpi-grid">
      <div class="kpi"><div class="kpi-label">Contract Value</div><div class="kpi-value">${formatCurrency(contract.value)}</div></div>
      <div class="kpi"><div class="kpi-label">SLA Met</div><div class="kpi-value" style="color:#0d9488">${slaMet}</div></div>
      <div class="kpi"><div class="kpi-label">SLA At Risk</div><div class="kpi-value" style="color:#d97706">${slaAtRisk}</div></div>
      <div class="kpi"><div class="kpi-label">SLA Breached</div><div class="kpi-value" style="color:${slaBreaches>0?'#dc2626':'#0d9488'}">${slaBreaches}</div></div>
    </div>
    <h2>Scope of Work</h2>
    <div class="scope">${contract.scopeOfWork}</div>
    <h2>SLA / KPI Obligations</h2>
    <table><thead><tr><th>Metric</th><th>Target</th><th>Current</th><th>Status</th></tr></thead><tbody>${slaRows}</tbody></table>
    <h2>Deliverables</h2>
    <table><thead><tr><th>Title</th><th>Owner</th><th>Due Date</th><th>Status</th></tr></thead><tbody>${deliverableRows}</tbody></table>
    <h2>Clause Library</h2>
    <table><thead><tr><th>Type</th><th>Title</th><th>Included</th><th>Last Reviewed</th></tr></thead><tbody>${clauseRows}</tbody></table>
    <div class="footer"><span>City of Pleasanton TPRM · Contract Summary</span><span>Generated ${new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}</span></div>
    </body></html>`;

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => { win.print(); }, 400);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[hsl(var(--surface))] shadow-2xl flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[hsl(var(--border))] flex-shrink-0">
          <div className="min-w-0 flex-1 pr-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono text-[hsl(var(--text-muted))]">{contract.contractRef}</span>
              <StatusBadge status={contract.status} />
              {contract.status === 'Expiring' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400">
                  <Zap size={10} /> {contract.daysUntilExpiry}d left
                </span>
              )}
            </div>
            <h2 className="text-base font-semibold text-[hsl(var(--text-primary))] leading-snug">{contract.title}</h2>
            <p className="text-sm text-[hsl(var(--text-muted))] mt-0.5">{contract.vendorName} · {contract.department}</p>
          </div>
          <button onClick={onClose} className="btn-ghost p-1.5 flex-shrink-0">
            <X size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[hsl(var(--border))] px-4 flex-shrink-0 overflow-x-auto">
          {tabs.map((tab) => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'border-[hsl(var(--primary))] text-[hsl(var(--primary))]'
                    : 'border-transparent text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))]'
                }`}
              >
                <TabIcon size={13} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {activeTab === 'overview' && <OverviewTab contract={contract} />}
          {activeTab === 'deliverables' && <DeliverablesTab contract={contract} />}
          {activeTab === 'sla' && <SLATab contract={contract} />}
          {activeTab === 'clauses' && <ClausesTab contract={contract} />}
          {activeTab === 'timeline' && <TimelineTab contract={contract} />}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-[hsl(var(--border))] p-4 flex items-center gap-2 flex-shrink-0">
          <button className="btn-primary text-xs flex items-center gap-1.5">
            <Edit3 size={13} /> Edit Contract
          </button>
          <button
            onClick={handleExportPDF}
            className="btn-secondary text-xs flex items-center gap-1.5"
          >
            <Download size={13} /> Export PDF
          </button>
          <button className="btn-ghost text-xs flex items-center gap-1.5">
            <Copy size={13} /> Duplicate
          </button>
        </div>
      </div>
    </div>
  );
}

function OverviewTab({ contract }: { contract: Contract }) {
  return (
    <div className="space-y-5">
      {/* Key Details Grid */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: 'Contract Value', value: formatCurrency(contract.value), icon: TrendingUp },
          { label: 'Owner', value: contract.owner, icon: User },
          { label: 'Start Date', value: formatDate(contract.startDate), icon: Calendar },
          { label: 'End Date', value: formatDate(contract.endDate), icon: Calendar },
          { label: 'Renewal Date', value: formatDate(contract.renewalDate), icon: Clock },
          { label: 'Auto-Renew', value: contract.autoRenew ? 'Yes' : 'No', icon: RefreshIcon },
          { label: 'Category', value: contract.category, icon: Tag },
          { label: 'Signed By', value: contract.signedBy || 'Not yet signed', icon: CheckCircle2 },
        ].map(({ label, value, icon: Icon }) => (
          <div key={label} className="bg-[hsl(var(--surface-raised))] rounded-lg p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Icon size={12} className="text-[hsl(var(--text-muted))]" />
              <span className="text-2xs text-[hsl(var(--text-muted))] uppercase tracking-wide font-medium">{label}</span>
            </div>
            <p className="text-sm font-medium text-[hsl(var(--text-primary))]">{value}</p>
          </div>
        ))}
      </div>

      {/* Scope of Work */}
      <div>
        <h3 className="text-xs font-semibold text-[hsl(var(--text-primary))] uppercase tracking-wide mb-2 flex items-center gap-1.5">
          <Layers size={13} className="text-[hsl(var(--primary))]" /> Scope of Work
        </h3>
        <p className="text-sm text-[hsl(var(--text-secondary))] leading-relaxed bg-[hsl(var(--surface-raised))] rounded-lg p-3">
          {contract.scopeOfWork}
        </p>
      </div>
    </div>
  );
}

function RefreshIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </svg>
  );
}

function DeliverablesTab({ contract }: { contract: Contract }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-xs font-semibold text-[hsl(var(--text-primary))] uppercase tracking-wide flex items-center gap-1.5">
          <CheckSquare size={13} className="text-[hsl(var(--primary))]" /> Deliverables ({contract.deliverables.length})
        </h3>
        <span className="text-xs text-[hsl(var(--text-muted))]">
          {contract.deliverables.filter(d => d.status === 'Completed').length} of {contract.deliverables.length} completed
        </span>
      </div>
      {contract.deliverables.map((d) => {
        const cfg = DELIVERABLE_STATUS_CONFIG[d.status];
        return (
          <div key={d.id} className="bg-[hsl(var(--surface-raised))] rounded-lg p-3 flex items-start gap-3">
            <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
              d.status === 'Completed' ? 'bg-teal-500' :
              d.status === 'Overdue' ? 'bg-red-500' :
              d.status === 'In Progress' ? 'bg-blue-500' : 'bg-slate-400'
            }`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium text-[hsl(var(--text-primary))]">{d.title}</p>
                <span className={`text-2xs font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ${cfg.text} ${cfg.bg}`}>
                  {d.status}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-xs text-[hsl(var(--text-muted))] flex items-center gap-1">
                  <Calendar size={10} /> Due {formatDate(d.dueDate)}
                </span>
                <span className="text-xs text-[hsl(var(--text-muted))] flex items-center gap-1">
                  <User size={10} /> {d.owner}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SLATab({ contract }: { contract: Contract }) {
  return (
    <div className="space-y-3">
      <h3 className="text-xs font-semibold text-[hsl(var(--text-primary))] uppercase tracking-wide flex items-center gap-1.5 mb-1">
        <BarChart2 size={13} className="text-[hsl(var(--primary))]" /> SLA & KPI Obligations
      </h3>
      {contract.slaObligations.map((sla, i) => {
        const cfg = SLA_STATUS_CONFIG[sla.status];
        return (
          <div key={i} className="bg-[hsl(var(--surface-raised))] rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-[hsl(var(--text-primary))]">{sla.metric}</p>
              <span className={`inline-flex items-center gap-1 text-2xs font-medium px-2 py-0.5 rounded-full ${cfg.text} ${cfg.bg}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                {sla.status}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <div>
                <p className="text-2xs text-[hsl(var(--text-muted))] mb-0.5">Target</p>
                <p className="text-xs font-semibold text-[hsl(var(--text-primary))]">{sla.target}</p>
              </div>
              <ArrowRight size={12} className="text-[hsl(var(--text-muted))]" />
              <div>
                <p className="text-2xs text-[hsl(var(--text-muted))] mb-0.5">Current</p>
                <p className={`text-xs font-semibold ${sla.status === 'Breached' ? 'text-red-600 dark:text-red-400' : sla.status === 'At Risk' ? 'text-amber-600 dark:text-amber-400' : 'text-teal-600 dark:text-teal-400'}`}>
                  {sla.current}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ClausesTab({ contract }: { contract: Contract }) {
  return (
    <div className="space-y-3">
      <h3 className="text-xs font-semibold text-[hsl(var(--text-primary))] uppercase tracking-wide flex items-center gap-1.5 mb-1">
        <BookOpen size={13} className="text-[hsl(var(--primary))]" /> Clause Library
      </h3>
      {contract.clauses.map((clause) => {
        const ClauseIcon = CLAUSE_ICONS[clause.type];
        const colorClass = CLAUSE_COLORS[clause.type];
        return (
          <div key={clause.id} className={`rounded-lg border p-3 ${clause.included ? 'border-[hsl(var(--border))] bg-[hsl(var(--surface-raised))]' : 'border-dashed border-[hsl(var(--border))] opacity-60'}`}>
            <div className="flex items-start gap-2.5">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                <ClauseIcon size={13} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <p className="text-sm font-medium text-[hsl(var(--text-primary))]">{clause.title}</p>
                  <span className={`text-2xs font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                    clause.included
                      ? 'bg-teal-50 dark:bg-teal-950/40 text-teal-700 dark:text-teal-400' :'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                  }`}>
                    {clause.included ? '✓ Included' : '— Excluded'}
                  </span>
                </div>
                <span className={`text-2xs font-medium px-1.5 py-0.5 rounded-md ${colorClass} mb-1.5 inline-block`}>
                  {clause.type}
                </span>
                <p className="text-xs text-[hsl(var(--text-secondary))] leading-relaxed">{clause.summary}</p>
                <p className="text-2xs text-[hsl(var(--text-muted))] mt-1.5">Last reviewed: {formatDate(clause.lastReviewed)}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TimelineTab({ contract }: { contract: Contract }) {
  return (
    <div className="space-y-1">
      <h3 className="text-xs font-semibold text-[hsl(var(--text-primary))] uppercase tracking-wide flex items-center gap-1.5 mb-3">
        <Calendar size={13} className="text-[hsl(var(--primary))]" /> Contract Timeline
      </h3>
      <div className="relative">
        <div className="absolute left-4 top-0 bottom-0 w-px bg-[hsl(var(--border))]" />
        <div className="space-y-4">
          {contract.timeline.map((event, i) => {
            const EventIcon = TIMELINE_ICONS[event.type];
            const colorClass = TIMELINE_COLORS[event.type];
            return (
              <div key={i} className="flex gap-3 relative">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${colorClass}`}>
                  <EventIcon size={13} />
                </div>
                <div className="flex-1 pb-1">
                  <p className="text-sm font-medium text-[hsl(var(--text-primary))] leading-snug">{event.event}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-[hsl(var(--text-muted))]">{formatDate(event.date)}</span>
                    <span className="text-2xs text-[hsl(var(--text-muted))]">·</span>
                    <span className="text-xs text-[hsl(var(--text-muted))]">{event.actor}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ContractModule() {
  const { can } = useRole();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ContractStatus | 'All'>('All');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  const filtered = useMemo(() => {
    return MOCK_CONTRACTS.filter((c) => {
      const matchSearch = !search || c.title.toLowerCase().includes(search.toLowerCase()) || c.vendorName.toLowerCase().includes(search.toLowerCase()) || c.contractRef.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'All' || c.status === statusFilter;
      const matchCategory = categoryFilter === 'All' || c.category === categoryFilter;
      return matchSearch && matchStatus && matchCategory;
    });
  }, [search, statusFilter, categoryFilter]);

  const kpis = useMemo(() => ({
    total: MOCK_CONTRACTS.length,
    active: MOCK_CONTRACTS.filter(c => c.status === 'Active').length,
    expiring: MOCK_CONTRACTS.filter(c => c.status === 'Expiring').length,
    pendingSig: MOCK_CONTRACTS.filter(c => c.status === 'Pending Signature').length,
    totalValue: MOCK_CONTRACTS.reduce((sum, c) => sum + c.value, 0),
    slaBreaches: MOCK_CONTRACTS.flatMap(c => c.slaObligations).filter(s => s.status === 'Breached').length,
  }), []);

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('All');
    setCategoryFilter('All');
  };

  const hasFilters = search || statusFilter !== 'All' || categoryFilter !== 'All';

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--background))]">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))] flex-shrink-0">
        <div className="flex items-center gap-3">
          <Link
            href="/executive-dashboard"
            className="btn-ghost p-1.5 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))]"
            title="Back to Dashboard"
          >
            <ArrowLeft size={16} />
          </Link>
          <div>
            <h1 className="text-lg font-bold text-[hsl(var(--text-primary))]">Contracts</h1>
            <p className="text-xs text-[hsl(var(--text-muted))] mt-0.5">{MOCK_CONTRACTS.length} contracts · {formatCurrency(kpis.totalValue)} total portfolio value</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {can('vendor:create') && (
            <button className="btn-primary text-xs flex items-center gap-1.5">
              <Plus size={13} /> New Contract
            </button>
          )}
          {can('dashboard:export') && (
            <button className="btn-secondary text-xs flex items-center gap-1.5">
              <Download size={13} /> Export
            </button>
          )}
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-6 gap-3 px-6 py-4 flex-shrink-0">
        <KPICard label="Total Contracts" value={kpis.total} sub="All statuses" color="text-[hsl(var(--text-primary))]" />
        <KPICard label="Active" value={kpis.active} sub="In force" color="text-teal-600 dark:text-teal-400" />
        <KPICard label="Expiring Soon" value={kpis.expiring} sub="Action required" color="text-red-600 dark:text-red-400" />
        <KPICard label="Pending Signature" value={kpis.pendingSig} sub="Awaiting execution" color="text-purple-600 dark:text-purple-400" />
        <KPICard label="Portfolio Value" value={formatCurrency(kpis.totalValue)} sub="Total contract value" color="text-[hsl(var(--primary))]" />
        <KPICard label="SLA Breaches" value={kpis.slaBreaches} sub="Across all contracts" color={kpis.slaBreaches > 0 ? 'text-red-600 dark:text-red-400' : 'text-teal-600 dark:text-teal-400'} />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 px-6 pb-4 flex-shrink-0">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
          <input
            type="text"
            placeholder="Search contracts, vendors..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-8 text-sm h-8 w-full"
          />
        </div>

        {/* Status Filter */}
        <div className="relative">
          <button
            onClick={() => { setShowStatusDropdown(!showStatusDropdown); setShowCategoryDropdown(false); }}
            className="btn-secondary text-xs h-8 flex items-center gap-1.5"
          >
            <Filter size={12} />
            {statusFilter === 'All' ? 'All Statuses' : statusFilter}
            <ChevronDown size={11} />
          </button>
          {showStatusDropdown && (
            <div className="absolute top-full mt-1 left-0 z-20 bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg shadow-lg py-1 min-w-[160px]">
              {(['All', ...ALL_STATUSES] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => { setStatusFilter(s); setShowStatusDropdown(false); }}
                  className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[hsl(var(--surface-raised))] ${statusFilter === s ? 'text-[hsl(var(--primary))] font-medium' : 'text-[hsl(var(--text-primary))]'}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Category Filter */}
        <div className="relative">
          <button
            onClick={() => { setShowCategoryDropdown(!showCategoryDropdown); setShowStatusDropdown(false); }}
            className="btn-secondary text-xs h-8 flex items-center gap-1.5"
          >
            <Tag size={12} />
            {categoryFilter}
            <ChevronDown size={11} />
          </button>
          {showCategoryDropdown && (
            <div className="absolute top-full mt-1 left-0 z-20 bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg shadow-lg py-1 min-w-[180px]">
              {ALL_CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => { setCategoryFilter(cat); setShowCategoryDropdown(false); }}
                  className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[hsl(var(--surface-raised))] ${categoryFilter === cat ? 'text-[hsl(var(--primary))] font-medium' : 'text-[hsl(var(--text-primary))]'}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>

        {hasFilters && (
          <button onClick={clearFilters} className="btn-ghost text-xs h-8 flex items-center gap-1 text-[hsl(var(--text-muted))]">
            <X size={11} /> Clear
          </button>
        )}

        <span className="text-xs text-[hsl(var(--text-muted))] ml-auto">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Contract Table */}
      <div className="flex-1 overflow-auto px-6 pb-6">
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--surface-raised))]">
                {['Contract', 'Vendor', 'Status', 'Value', 'End Date', 'Owner', 'SLA Health', ''].map((h) => (
                  <th key={h} className="text-left px-4 py-2.5 text-2xs font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wide whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-sm text-[hsl(var(--text-muted))]">
                    No contracts match your filters
                  </td>
                </tr>
              ) : (
                filtered.map((contract) => {
                  const slaBreaches = contract.slaObligations.filter(s => s.status === 'Breached').length;
                  const slaAtRisk = contract.slaObligations.filter(s => s.status === 'At Risk').length;
                  const daysLeft = contract.endDate ? daysUntil(contract.endDate) : null;

                  return (
                    <tr
                      key={contract.id}
                      onClick={() => setSelectedContract(contract)}
                      className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-raised))] cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-[hsl(var(--text-primary))] leading-snug">{contract.title}</p>
                          <p className="text-xs text-[hsl(var(--text-muted))] font-mono mt-0.5">{contract.contractRef}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Building2 size={12} className="text-[hsl(var(--text-muted))]" />
                          <span className="text-sm text-[hsl(var(--text-secondary))]">{contract.vendorName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={contract.status} />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-semibold text-[hsl(var(--text-primary))]">{formatCurrency(contract.value)}</span>
                      </td>
                      <td className="px-4 py-3">
                        {contract.endDate ? (
                          <div>
                            <p className="text-sm text-[hsl(var(--text-secondary))]">{formatDate(contract.endDate)}</p>
                            {daysLeft !== null && daysLeft <= 90 && daysLeft > 0 && (
                              <p className={`text-xs mt-0.5 ${daysLeft <= 30 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
                                {daysLeft}d remaining
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-[hsl(var(--text-muted))]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <User size={12} className="text-[hsl(var(--text-muted))]" />
                          <span className="text-sm text-[hsl(var(--text-secondary))]">{contract.owner}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {contract.slaObligations.length === 0 ? (
                          <span className="text-xs text-[hsl(var(--text-muted))]">N/A</span>
                        ) : slaBreaches > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/40 px-2 py-0.5 rounded-full">
                            <AlertTriangle size={10} /> {slaBreaches} breach{slaBreaches > 1 ? 'es' : ''}
                          </span>
                        ) : slaAtRisk > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 px-2 py-0.5 rounded-full">
                            <AlertCircle size={10} /> {slaAtRisk} at risk
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 dark:text-teal-400 bg-teal-50 dark:bg-teal-950/40 px-2 py-0.5 rounded-full">
                            <CheckCircle2 size={10} /> All met
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedContract(contract); }}
                          className="btn-ghost p-1 opacity-0 group-hover:opacity-100"
                        >
                          <Eye size={14} />
                        </button>
                        <ChevronRight size={14} className="text-[hsl(var(--text-muted))]" />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Panel */}
      {selectedContract && (
        <ContractDetailPanel
          contract={selectedContract}
          onClose={() => setSelectedContract(null)}
        />
      )}
    </div>
  );
}
